<template>
  <div class="flex min-h-[calc(100vh-5rem)] items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
    <div class="w-full max-w-md">
      <!-- État de chargement -->
      <div v-if="loading" class="bg-white p-8 rounded-xl shadow-md text-center">
        <div class="inline-block">
          <div class="animate-spin rounded-full h-12 w-12 border-4 border-zebu-orange border-b-transparent"></div>
        </div>
        <p class="mt-4 text-lg font-medium text-gray-900">Vérification de votre email en cours...</p>
      </div>

      <!-- État de succès -->
      <div v-if="success" class="bg-white p-8 rounded-xl shadow-md space-y-6">
        <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
          <svg class="h-6 w-6 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" />
          </svg>
        </div>
        <div class="text-center">
          <h3 class="mt-2 text-lg font-semibold text-gray-900">Email vérifié avec succès</h3>
          <p class="mt-2 text-gray-600">Votre adresse email a été validée. Vous pouvez maintenant vous connecter à votre compte.</p>
        </div>
        <div class="mt-6">
          <router-link
            to="/login"
            class="flex w-full justify-center rounded-lg bg-zebu-orange px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-zebu-orange/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-zebu-orange"
          >
            Se connecter
          </router-link>
        </div>
      </div>

      <!-- État d'erreur -->
      <div v-if="error" class="bg-white p-8 rounded-xl shadow-md space-y-6">
        <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
          <svg class="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
        </div>
        <div class="text-center">
          <h3 class="mt-2 text-lg font-semibold text-gray-900">Erreur de vérification</h3>
          <p class="mt-2 text-gray-600">{{ errorMessage }}</p>
          
          <!-- Option de renvoi si le token est expiré -->
          <div v-if="isTokenExpired" class="mt-4">
            <button
              @click="handleResendVerification"
              :disabled="isResending"
              class="text-sm text-zebu-orange hover:text-zebu-orange/90 font-medium"
            >
              {{ isResending ? 'Envoi en cours...' : 'Renvoyer un nouveau lien de vérification' }}
            </button>
          </div>
        </div>
        <div class="mt-6 space-y-4">
          <router-link
            to="/login"
            class="flex w-full justify-center rounded-lg bg-zebu-orange px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-zebu-orange/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-zebu-orange"
          >
            Retour à la connexion
          </router-link>
          
          <!-- Bouton réessayer en cas d'erreur serveur -->
          <button
            v-if="!isTokenExpired"
            @click="retryVerification"
            class="flex w-full justify-center rounded-lg border-2 border-zebu-orange px-4 py-3 text-sm font-semibold text-zebu-orange hover:bg-zebu-orange/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-zebu-orange"
          >
            Réessayer
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { memberareaService } from '@/services/memberarea.service';
import { useToast } from 'vue-toastification';

const route = useRoute();
const toast = useToast();
const loading = ref(true);
const success = ref(false);
const error = ref(false);
const errorMessage = ref('');
const isTokenExpired = ref(false);
const isResending = ref(false);

const verifyEmailToken = async (token) => {
  loading.value = true;
  error.value = false;
  
  try {
    const response = await memberareaService.verifyEmail(token);
    if (response.success) {
      success.value = true;
    } else {
      error.value = true;
      errorMessage.value = response.message;
      // Vérifie si le token est expiré basé sur le message
      isTokenExpired.value = response.message.toLowerCase().includes('expiré');
    }
  } catch (err) {
    error.value = true;
    errorMessage.value = err.response?.data?.message || "Une erreur est survenue lors de la vérification de votre email.";
    isTokenExpired.value = err.response?.status === 400;
  } finally {
    loading.value = false;
  }
};

const handleResendVerification = async () => {
  isResending.value = true;
  try {
    // Implémenter la logique de renvoi du lien de vérification
    // await memberareaService.resendVerificationEmail();
    toast.success("Un nouveau lien de vérification a été envoyé à votre adresse email.");
  } catch (err) {
    toast.error("Erreur lors de l'envoi du nouveau lien de vérification.");
  } finally {
    isResending.value = false;
  }
};

const retryVerification = () => {
  verifyEmailToken(route.params.token);
};

onMounted(() => {
  verifyEmailToken(route.params.token);
});
</script> 